import { createSelector } from 'reselect'
import { getIn, get, safeToJS, map } from 'utils/immutable'
import {
  vehicleReferenceParametersSelector,
  vehicleResourceDataSelector,
} from 'selectors/truck'
import { compareRpBaseNumber } from 'utils/referenceParameter'
import {
  hasConflictMessage,
  ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS,
  isFilterableConflict,
} from 'components/AddReferenceParameters/utils'

const addRPWFState = (state) => state.get('addReferenceParametersWorkflow')

export const getAddRPWPStarted = createSelector(addRPWFState, get('started'))

export const getIsFromCollection = createSelector(
  addRPWFState,
  get('isFromCollection')
)

const addRPWFSelectedRPsSelector = createSelector(
  addRPWFState,
  get('selectedRPs')
)

const currentCollectionSelector = createSelector(
  addRPWFState,
  get('currentCollection')
)

export const getCurrentCollection = createSelector(
  currentCollectionSelector,
  safeToJS
)

export const getAddRPWFSelectedRPs = createSelector(
  addRPWFSelectedRPsSelector,
  safeToJS
)

export const getHasSelectedRPs = createSelector(
  addRPWFSelectedRPsSelector,
  (rps) => Boolean(rps.size)
)

export const getResourceLimitsError = createSelector(
  addRPWFState,
  get('resourceLimitsError')
)

const addRPWFSearchSelector = createSelector(addRPWFState, get('search'))

export const getAddRPWFSearch = createSelector(addRPWFSearchSelector, safeToJS)

export const addRPWFStagedRPsSelector = createSelector(
  addRPWFState,
  get('stagedRPs')
)

export const getAddRPWFStagedRPs = createSelector(
  addRPWFStagedRPsSelector,
  safeToJS
)

export const getHasStagedRPs = createSelector(addRPWFStagedRPsSelector, (rps) =>
  Boolean(rps.size)
)

export const getIsAddingCollection = createSelector(
  addRPWFState,
  get('isAddingCollection')
)

export const getAddRPWFIsAddingRP = createSelector(
  addRPWFState,
  get('isAddingRP')
)

export const getCommitNote = createSelector(addRPWFState, get('commitNote'))

export const getHasCommitNote = createSelector(getCommitNote, Boolean)

export const getAddRPView = createSelector(addRPWFState, get('view'))

const addRPWFStagedRPUrisSelector = createSelector(
  addRPWFStagedRPsSelector,
  map(get('uri'))
)

export const getAddRPWFStagedRPsUris = createSelector(
  addRPWFStagedRPUrisSelector,
  safeToJS
)

export const getServiceRecordModalVisible = createSelector(
  addRPWFState,
  getIn(['serviceRecordModal', 'visible'])
)

export const getPinsNotAvailableModalVisible = createSelector(
  addRPWFState,
  getIn(['pinsNotAvailableModal', 'visible'])
)

const pinsNotAvailableModalCircuitsByRPsSelector = createSelector(
  addRPWFState,
  getIn(['pinsNotAvailableModal', 'circuitsByRPs'])
)

export const getPinsNotAvailableModalCircuitsByRPs = createSelector(
  pinsNotAvailableModalCircuitsByRPsSelector,
  safeToJS
)

export const getConfirmCircuitMappingModalVisible = createSelector(
  addRPWFState,
  getIn(['confirmCircuitMappingModal', 'visible'])
)

export const getAddRPWFVehicleSerialNumber = createSelector(
  addRPWFState,
  get('serialNumber')
)

export const getIsStagingRPs = createSelector(addRPWFState, get('isStagingRPs'))

const addRPSearchResultsSelector = createSelector(
  addRPWFSearchSelector,
  get('referenceParameters')
)

const resultsForAddRPSearchSelector = createSelector(
  vehicleReferenceParametersSelector,
  addRPWFStagedRPsSelector,
  addRPSearchResultsSelector,
  vehicleResourceDataSelector,
  (vehicleRPs, stagedRPs, searchRPs, resourceData) =>
    searchRPs.map((rp) =>
      processSearchRPs(rp, vehicleRPs, stagedRPs, resourceData)
    )
)

export const getSearchResultsForAddRPSearch = createSelector(
  resultsForAddRPSearchSelector,
  safeToJS
)

export const getFilteredSearchResultsForAddRPSearch = createSelector(
  getSearchResultsForAddRPSearch,
  getAddRPWFSearch,
  (results, search) => {
    if (!search || !search.filterConflicts) return results
    return results.filter((rp) => !isFilterableConflict(rp.disabled))
  }
)

const processSearchRPs = (searchRP, vehicleRPs, stagedRPs, resourceData) => {
  const conflictMessage = hasConflictMessage(
    safeToJS(searchRP),
    safeToJS(vehicleRPs),
    safeToJS(stagedRPs),
    get('timerCount', resourceData),
    get('logicCount', resourceData),
    get('maxTimerCount', resourceData),
    get('maxLogicCount', resourceData)
  )
  const staged = conflictMessage === ADD_RP_CONFLICT_MESSAGE_TRANSLATIONS.STAGED
  const replacement = vehicleRPs.find((vrp) =>
    compareRpBaseNumber(safeToJS(searchRP), safeToJS(vrp))
  )
  return searchRP
    .set('disabled', conflictMessage)
    .set('staged', staged)
    .set('replacing', replacement)
}

export const getSelectedRPs = createSelector()
